import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { feedParamsSchema } from "@wags/discovery/index.ts";
import { getDiscovery } from "@/lib/core/discovery-core.server.ts";

export const PUPPY_MAX_AGE_MS = 12 * 30 * 24 * 60 * 60 * 1000;

export type ExploreFeedParams = {
    lat: number;
    lng: number;
    maxDistanceMiles?: number;
    page?: number;
    limit?: number;
};

const DEFAULT_LIMIT = 24;

async function fetchExploreFeedPage(data: ExploreFeedParams) {
    const discovery = await getDiscovery();
    return await discovery.feeds.exploreFeed(data);
}

const searchTopPuppies = createServerFn({ method: "GET" })
    .inputValidator((data: ExploreFeedParams) => feedParamsSchema.parse(data))
    .handler(async ({ data }) => await fetchExploreFeedPage(data));

type ExploreFeedPageParam = ExploreFeedParams & { page: number; limit: number };

export const useInfiniteExplore = (params: ExploreFeedParams | undefined) => {
    return useInfiniteQuery({
        enabled: Boolean(params),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        queryKey: ["exploreFeed", params],
        queryFn: async ({ pageParam }) => {
            return await searchTopPuppies({ data: pageParam });
        },
        initialPageParam: params
            ? { ...params, page: 0, limit: params.limit ?? DEFAULT_LIMIT }
            : ({
                  lat: 0,
                  lng: 0,
                  page: 0,
                  limit: DEFAULT_LIMIT,
              } as ExploreFeedPageParam),
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            if (lastPage.results.length === 0 || !lastPage.nextPage) {
                return undefined;
            }
            return { ...lastPageParam, page: lastPage.nextPage };
        },
        getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
            if (firstPageParam.page === 0) return undefined;
            return { ...firstPageParam, page: firstPageParam.page - 1 };
        },
    });
};
